/**
 * Egzersiz üreticisinin tür sözleşmesi.
 *
 * Tek bir üretim üç kanala birden hizmet eder: ekranda oynanır, A4'e basılır,
 * veli telefonunda açılır. Bunun mümkün olması için şablonlar "nasıl
 * çizilecek" demez, "ne var" der — çizim modeli soyut tutulur. HTML, canvas
 * ve PDF aynı modeli okur.
 *
 * GETAP'ın 10.500 sabit sayfası yerine buradaki yaklaşım: 41 şablon ×
 * parametre × tohum = pratikte sınırsız varyant. Aynı tohum aynı sayfayı
 * verir, farklı tohum yeni sayfa.
 */

import type { Domain } from '../types'
import type { ShapeName } from '../render/shapes'
import type { AlanKodu } from '../render/icons'
import type { GozCizimi } from '../render/gozler'

export type ExerciseCode =
  // Dikkat
  | 'scan_target'
  | 'spot_difference'
  | 'odd_one_out'
  | 'word_search'
  | 'path_trace'
  // Bellek
  | 'span_recall'
  | 'memory_pairs'
  | 'symbol_code'
  | 'listening_steps'
  | 'reading_target'
  // Akıl yürütme
  | 'matrix_pattern'
  | 'sequence_complete'
  | 'logic_grid'
  | 'sudoku_mini'
  | 'category_sort'
  // Uzamsal ve hız
  | 'maze'
  | 'mental_rotation'
  | 'mirror_image'
  | 'arith_fluency'
  | 'opposite_rule'
  /**
   * 2026-11-04'te eklenenler.
   *
   * Yirmi şablonun üzerine, uygulayıcıların adını saydığı klasik
   * ölçüm/alıştırma biçimleri. Her biri kendi dosyasında değil,
   * yakın olduğu dosyada duruyor; dosya düzeni gruplama değil.
   */
  // Nicel muhakeme
  | 'dual_sequence'
  | 'arith_grid'
  // Hiz ve esneklik
  | 'trail_making'
  // Sozel akicilik
  | 'word_ladder'
  | 'word_fluency'
  // Uzamsal kopyalama
  | 'dot_copy'
  // Gecikmeli bellek
  | 'delayed_recall'
  // Figur-zemin
  | 'overlap_find'
  | 'hidden_figure'
  // Yonetici islevler
  | 'tower_of_london'
  | 'rule_maze'
  // Dil ve gunluk yasam
  | 'code_language'
  | 'contradiction'
  | 'schedule_plan'
  | 'route_plan'
  // Sosyal bilis
  | 'false_belief'
  | 'social_cue'
  // Iraksak dusunme (kagit, elle puanlanir)
  | 'alternative_uses'
  | 'incomplete_figures'
  // Ince motor
  | 'track_path'
  | 'rhythm_tap'
  // Goz cevresinden ifade okuma
  | 'eye_emotion'
  /**
   * 2026-11-04, ikinci grup: tumdengelim, nicel muhakeme ve uzamsal
   * sentez. Hepsinin ortak yani kurgunun GERIYE DOGRU uretilmesi:
   * once cozum, sonra cozumden turetilmis ipuclari/katmanlar.
   */
  | 'zebra_grid'
  | 'balance_scale'
  | 'overlay_layers'
  | 'paper_fold'
  | 'flow_trace'

export type Channel = 'digital' | 'print'

/* ------------------------------------------------------------------ *
 * Çizim modeli — altı düğüm tipi, hepsi hem ekranda hem kağıtta çizilir
 * ------------------------------------------------------------------ */

export interface CellNode {
  /**
   * Hücrede görünen METİN: harf, rakam, kelime.
   *
   * Şekil buraya yazılmaz; şeklin yeri `shape`. İkisi aynı alanda
   * taşınabilirdi (şekil adları harf ve rakamlarla çakışmıyor) ama o
   * zaman "bu hücre ne gösteriyor" sorusunun cevabı türde değil,
   * çalışma anında bir arama tablosunda olurdu.
   */
  glyph?: string
  /**
   * Hücrede çizilen ŞEKİL.
   *
   * Geometrisi `render/shapes.ts`'te; ekran ve kâğıt aynı yoldan
   * çiziyor. Eskiden şekiller de `glyph` içinde Unicode karakter
   * olarak duruyordu ve görünüşleri yazı tipine bağlıydı — aynı
   * tohumun aynı sayfayı vermesi cihazlar arasında tutmuyordu.
   */
  shape?: ShapeName
  /** Doldurulacak boş kutu (cevap alanı) */
  blank?: boolean
  /** Vurgulu hücre (örnek, ipucu) */
  marked?: boolean
  /** Labirent duvarları: üst, sağ, alt, sol */
  walls?: [boolean, boolean, boolean, boolean]
  /** Hücre kimliği — dijital tarafta tıklama hedefi */
  id?: string
}

export type RenderNode =
  | { t: 'text'; value: string; size?: 'sm' | 'md' | 'lg'; bold?: boolean }
  | { t: 'grid'; cols: number; rows: number; cells: CellNode[]; gap?: number; border?: boolean }
  /** Serbest yerleşim: sembollerin x,y konumu (0–1 aralığında oran) */
  | {
      t: 'scatter'
      items: {
        glyph?: string
        shape?: ShapeName
        x: number
        y: number
        rot?: number
        id?: string
        /**
         * Uyaranın çevresine halka çizilsin mi.
         *
         * İz sürmede durak bir NOKTA: çocuk iki durağı birleştiriyor ve
         * çizginin nerede bitmesi gerektiğini halka söylüyor. Halkasız
         * bir rakam, çizginin rakamın hangi kenarında biteceğini
         * belirsiz bırakıyor; kâğıtta bu, uygulayıcının "birleştirdi
         * mi" kararını tartışmalı hale getiriyor.
         */
        ring?: boolean
        /**
         * Uyaranın göreli boyu (1 = varsayılan).
         *
         * Örtüşen şekillerde gerekiyor: şekiller birbirinin üstüne
         * biniyor ve kümenin okunurluğu boyutla ayarlanıyor. Bir
         * kümedeki şekillerin hepsi AYNI boyda — farklı boyda olsalar
         * küçük olan büyüğün içine tamamen düşebilir ve o şekli
         * görmek mümkün olmazdı. Eşit boyda, merkezleri ayrı iki
         * şekilden hiçbiri ötekini içine alamıyor.
         */
        size?: number
      }[]
      /** Alan yüksekliği (piksel). Verilmezse 150. */
      height?: number
      /**
       * Öğeler üst üste binsin mi.
       *
       * Normalde her öğe kendi kutusunda duruyor ve kutular çakışsa
       * bile üstteki alttakini kapatıyor. Figür-zemin görevinde ise
       * çizgilerin BİRBİRİNİN İÇİNDEN geçmesi gerekiyor: görevin
       * tamamı o kesişmeleri ayırmak.
       */
      overlap?: boolean
    }
  /**
   * Çizgiler (0–1 oranlı noktalar). Yol takibi gibi egzersizlerde
   * izlenecek şeyin kendisi. Sembolleri yerleştirip çizgileri unutmak,
   * çocuğa takip edilecek hiçbir şey olmayan bir sayfa vermek demekti.
   */
  | {
      t: 'paths'
      /**
       * `dash`: SVG stroke-dasharray değeri.
       *
       * İpucunu RENKTEN BAĞIMSIZ taşıyor. Renk tek başına yeterliydi
       * sanılıyordu; ev yazıcısı çoğunlukla siyah-beyaz bastığı için
       * kâğıtta ipucu sessizce kayboluyordu ve kolay olması gereken
       * seviye, olması gerekenden zor çıkıyordu. Çizgi biçimi gri
       * tonlamada da ayırt ediliyor.
       */
      items: { points: [number, number][]; id?: string; color?: string; dash?: string }[]
      labels?: { x: number; y: number; text: string; id?: string }[]
      height?: number
      /**
       * Alan KARE ve çizim esnetilmiyor.
       *
       * Normalde çizim alanı sayfa genişliğinde ve SVG
       * `preserveAspectRatio="none"` ile esnetiliyor; yol takibinde
       * bu zararsız, çünkü orada anlam taşıyan şey çizginin hangi
       * uçtan hangi uca gittiği.
       *
       * Koridorda ise anlam taşıyan şey GENİŞLİK: esnetilmiş bir
       * koridorun yatay bölümleri dar, dikey bölümleri geniş
       * görünüyor ve "her yerde aynı genişlik" sözü bozuluyor.
       * Zorluk, koridorun darlığından geliyordu.
       */
      kare?: boolean
    }
  /** Anahtar/açıklama: sembol → karşılık */
  /**
   * Anahtar/açıklama: sembol → karşılık.
   *
   * `from` metin, `fromShape` şekil; biri dolu olur. Şifre
   * egzersizinin anahtarı şekillerden kuruluyor, yol takibininki
   * harflerden.
   */
  | { t: 'key'; pairs: { from?: string; fromShape?: ShapeName; to: string }[] }
  /**
   * Satır satır sorular (aritmetik vb.).
   *
   * `palette` verilirse cevap klavyeyle değil, verilen semboller arasından
   * DOKUNARAK girilir. Şekil dizisi isteyen bir egzersizi kağıtta çocuk
   * çizer, ekranda ise ▲ karakterini klavyede bulamaz — palet olmadan o
   * egzersiz ekranda cevaplanamaz hale geliyordu.
   */
  | {
      t: 'rows'
      /**
       * `label` satırın metni. Satırın kendisi bir ŞEKİLSE
       * (`symbol_code`'da soru sembolden ibaret) şekil `labelShape`'e
       * yazılır ve `label` boş kalır.
       */
      items: {
        label: string
        labelShape?: ShapeName
        blank: boolean
        id?: string
        /**
         * Cevap kutusu geniş olsun mu.
         *
         * Varsayılan kutu 34 piksel: bir rakam ya da kısa bir kelime
         * için doğru ölçü. Rota planlamada cevap sekiz durak adı ve o
         * kutuya sığmıyor — çocuk satırın dışına taşarak yazıyor,
         * uygulayıcı da okuyamıyor.
         */
        genis?: boolean
      }[]
      columns?: number
      /**
       * Seçilebilir uyaranlar. Şekil paletinde `shape`, metin
       * paletinde `glyph` dolu olur; cevaba giden dize ikisinde de
       * `shape ?? glyph`.
       */
      palette?: { glyph?: string; shape?: ShapeName }[]
      /**
       * Paletten seçim satırı doldurur ('single', varsayılan) mı yoksa
       * sırayla eklenir mi ('sequence'). Dizi hatırlamada çocuk birden
       * çok sembol girer; grup ayırmada tek bir ad yeter.
       */
      paletteMode?: 'single' | 'sequence'
    }
  /** Seçenekler: çoktan seçmeli */
  | { t: 'choices'; items: { id: string; glyph?: string; shape?: ShapeName }[] }
  /**
   * Desen döşemeleri — kare hücre matrisi olarak çizilen küçük şekiller.
   *
   * `on[r * n + c]` dolu hücreyi gösteriyor. Döndürme ve aynalama
   * ÜRETİMDE matris üzerinde yapılıyor, çizimde CSS dönüşümüyle değil.
   *
   * Gerekçe: `zihinde çevirme` egzersizi bir metin glifini (`◣`) döndürmek
   * üzerine kuruluydu ve açı çizim ağacına hiç ulaşmıyordu — `choices`
   * düğümünün dönme alanı yok. Doğru şık her tohumda temel şeklin birebir
   * aynısı çıkıyordu; yönerge "döndürülmüş hali hangisi" diyor, cevap ise
   * "aynısı" oluyordu. Dönüşümü glife uygulayarak düzeltmek, sorunu yazı
   * tipine taşımak olurdu: eğik çizilen bir glifin taban çizgisi ve sınır
   * kutusu fonta göre oynuyor, tablette ve yazıcıda farklı duruyor.
   * Matris hiçbirine bağlı değil.
   *
   * Yan kazanç: dönme yalnızca 90°'nin katları olabiliyor. Bu bir kısıt
   * değil, doğru olan: ara açı matris üzerinde tanımsız ve eski kodda
   * doğru şıkkı tek "eğik duran" şık yapıp görevi bakmakla çözülür hale
   * getiriyordu.
   */
  | {
      t: 'tiles'
      /** Sutun sayisi */
      n: number
      /**
       * Satir sayisi; verilmezse `n` (kare izgara).
       *
       * Katlanmis kagit KARE DEGIL: iki kez katlanan bir kare, dortte
       * bir boyunda bir dikdortgen. Kare cizilseydi cocuk delikleri
       * kagidin yanlis yerinde gorurdu.
       */
      satir?: number
      items: { id: string; on: boolean[]; caption?: string }[]
      pick?: boolean
    }
  /**
   * Nokta ızgarası ve üzerine çizilmiş kırık çizgi.
   *
   * `model` nokta indekslerinden (r * n + c) oluşan bir yol. Komşuluk
   * şartı üretimde: iki ardışık nokta YANYANA (çapraz dahil). Uzağa
   * atlayan bir çizgi aradaki noktanın üstünden geçiyor ve "bu çizgi o
   * noktaya uğruyor mu" sorusu cevapsız kalıyor — kopyalanacak şekil
   * belirsiz hale geliyor.
   *
   * `copy` verilirse çizim alanı da basılıyor: solda model, sağda aynı
   * boyda boş ızgara. Ekranda sağdaki ızgara dokunarak çiziliyor.
   */
  /**
   * Çizim kutuları: her kutuda anlamsız bir çizgi parçası.
   *
   * Yarım çizgiyi tamamlama görevi için. `paths` ile anlatılamıyor:
   * orada tek bir çizim alanı var, burada YAN YANA birçok küçük kutu
   * ve her birinin kendi koordinat sistemi gerekiyor.
   *
   * `strokes` her kutu için kırık çizgi listesi, 0–1 aralığında.
   * `adSatiri` kutunun altına "Adı: ____" satırı koyuyor: Torrance
   * görevinde resme ad vermek görevin parçası, süs değil.
   */
  /**
   * Ritim: vuruşlar arası süreler (ms).
   *
   * Uyaranın kendisi SES ve zaman; çizilecek bir şey yok. Yine de
   * çizim ağacında duruyor çünkü ağaç "ne var" diyor, "nasıl
   * çizilecek" demiyor: ekran bunu çalıyor, kâğıt kanalı ise bu
   * şablonu hiç almıyor.
   *
   * `araliklar[i]`, i. vuruşla i+1. vuruş arasındaki süre. N vuruş
   * için N-1 aralık var.
   */
  | {
      t: 'rhythm'
      araliklar: number[]
    }
  /**
   * Şematik göz çevresi çizimleri.
   *
   * Geometrisi `render/gozler.ts`'te; yazdırma ve çocuk uygulaması aynı
   * fonksiyonu çağırıyor. Düğüm DUYGU ADI TAŞIMIYOR, yalnızca sayı:
   * taşısaydı sayfanın cevabı yazdırma HTML'inin içinde yazardı.
   *
   * `etiket` kâğıttaki harf (A, B, C). Çizimler arasından seçme
   * sorusunda çocuk harfi yazıyor — hem kâğıtta hem ekranda, çünkü iki
   * kanalın aynı soruyu sorması, cevabı aynı biçimde almasından daha
   * önemli değil: ikisi de harf.
   */
  | {
      t: 'eyes'
      cols: number
      items: { cizim: GozCizimi; etiket?: string; id?: string }[]
    }
  /**
   * Terazi kefeleri.
   *
   * Her terazi iki kefe ve her kefe bir sembol yigini. `soru: true`
   * olan terazinin SAG kefesi bilinmiyor: `soruSekli` sembolu
   * veriliyor, kac tane oldugu sorulmuyor degil, cevap o.
   *
   * Dugum SAYI TASIMIYOR: sembollerin gizli degerleri burada yok,
   * yalnizca cozumde. Tasisaydi sayfanin cevabi isaretlemenin icinde
   * yazardi.
   */
  | {
      t: 'scales'
      items: { sol: ShapeName[]; sag: ShapeName[]; soru?: boolean; soruSekli?: ShapeName }[]
    }
  | {
      t: 'frames'
      cols: number
      items: { strokes: [number, number][][] }[]
      adSatiri?: boolean
    }
  | {
      t: 'dots'
      n: number
      /** Kırık çizgi: nokta indekslerinden oluşan yol */
      model: number[]
      /** Kopyalama alanı da basılsın mı */
      copy?: boolean
      /**
       * Arka plan deseni: sıralı olmayan kenar listesi.
       *
       * Gizli şekil görevinde desen tek bir kırık çizgi değil, bir
       * kenar yığını — `model` ile anlatılamıyor. Çocuk bu desenin
       * ÜZERİNDE, `model`'in kendisini çiziyor.
       */
      edges?: [number, number][]
      /** Desen üzerine çizim yapılacak mı (ekranda dokunulur) */
      trace?: boolean
      /**
       * Kutunun üstüne yazılan etiket.
       *
       * Gizli şekilde iki ızgara yan yana değil ALT ALTA duruyor ve
       * hangisinin aranan şekil hangisinin desen olduğu etiketten
       * anlaşılıyor. İkisi de aynı genişlikte basılıyor: aynı `n`,
       * aynı genişlik demek aynı nokta aralığı demek. Farklı aralıkta
       * basılsalardı çocuk şekli zihninde ölçeklendirmek zorunda
       * kalırdı ve görev figür-zemin olmaktan çıkıp ölçek
       * karşılaştırmaya dönerdi.
       */
      label?: string
    }

/* ------------------------------------------------------------------ */

export interface ExerciseParams {
  [key: string]: number | string | boolean
}

export interface ExerciseInstance {
  code: ExerciseCode
  version: string
  level: number
  seed: string
  params: ExerciseParams
  domains: Domain[]
  /** Çocuğa gösterilen yönerge */
  prompt: string
  /** İşitsel yönerge metni (varsa) — seslendirme için */
  spokenPrompt?: string
  render: RenderNode[]
  /** Doğru cevap. Kağıt kanalda uygulayıcı anahtarına basılır. */
  solution: unknown
  /** Dijital kanalda otomatik mi puanlanır */
  grading: 'auto' | 'manual'
  /** Tahmini süre — haftalık set dengelemek için */
  estimatedSeconds: number
  /** Kaç madde var: doğruluk oranının paydası */
  itemCount: number
}

export interface ExerciseTemplate {
  code: ExerciseCode
  title: string
  domains: Domain[]
  channels: Channel[]
  /** Zorluk 1–10 arası; her seviye için parametreler */
  params(level: number): ExerciseParams
  /** Aynı tohum + aynı seviye = aynı sayfa */
  generate(seed: number | bigint, level: number): ExerciseInstance
  /** Verilen cevabı puanlar (dijital kanal) */
  grade(instance: ExerciseInstance, answer: unknown): GradeResult
  /**
   * Cevap anahtarının kâğıttaki hali.
   *
   * Varsayılan biçimleyici çözümü düz bir liste sanıyor ve nesne
   * taşıyan çözümlerde ham JSON basıyordu: uygulayıcı
   * `[{"id":"m0_0","beklenen":"kama",...}]` okumak zorunda kalıyordu.
   * Çözümün nasıl okunacağını şablon biliyor; bilen yazsın.
   *
   * Yazmayan şablonlar varsayılanda kalıyor — sayı ve harf listeleri
   * için o zaten doğru.
   */
  anahtar?(instance: ExerciseInstance): string
}

/**
 * Tek bir maddenin sonucu.
 *
 * `perItem` yalnızca "hangi maddede yanlış yaptı" diyor. Uygulayıcının
 * sorduğu soru ise şu: NE yazdı? Boş mu bıraktı, yanlış mı yazdı,
 * yoksa doğruyu bulup başka yere mi koydu? Üçü üç ayrı bulgu ve
 * doğru/yanlış ikilisi üçünü de aynı gösteriyor.
 */
export interface ItemResult {
  /** Madde kimliği: hücre ya da satır id'si; yoksa sıra numarası */
  id: string
  /** İnsan okunur beklenen cevap */
  beklenen: string
  /** Çocuğun verdiği cevap; boş bırakıldıysa null */
  verilen: string | null
  dogru: boolean
  /**
   * Beklenmeyen fazladan işaret.
   *
   * Küme görevlerinde (hücre işaretleme) çocuk doğruları bulmanın yanı
   * sıra yanlış hücreleri de işaretleyebilir. Bunlar "eksik madde"
   * değil, fazladan işarettir ve ayrı okunmalı: hepsini işaretleyip tam
   * puan almaya çalışmak da bir davranış örüntüsü.
   */
  fazladan?: boolean
}

export interface GradeResult {
  correct: number
  total: number
  accuracy: number
  /** Madde madde doğru/yanlış — geriye dönük uyumluluk için duruyor */
  perItem?: boolean[]
  /** Madde madde detay: ne beklendi, ne verildi */
  items?: ItemResult[]
}

/**
 * Üretim sözleşmesinin sürümü.
 *
 * Aynı tohumun aynı sayfayı vermesi şablonun KODUNA bağlı: şablon
 * değişince eski bir atama aynı kodla aynı tohumdan BAŞKA bir sayfa
 * üretir. O yüzden şablonların çıktısı her değiştiğinde burası artıyor
 * ve eski atamalar `surum_uyusmazligi` ile reddediliyor — sessizce
 * başka bir egzersize dönüşmeleri yerine.
 *
 * 1.1: Katman 0. Şekiller Unicode karakter olmaktan çıkıp çizilen
 * şekillere döndü, çözüm dizeleri şekil adlarına dönüştü
 * (`'●'` değil `'daire'`). Aynı sürümde `mental_rotation` ve
 * `matrix_pattern` yeniden yazılmıştı; o değişiklikte sürüm
 * artırılmamıştı, burada birlikte kapanıyor.
 */
export const EXERCISE_VERSION = '1.1'

/** Seviye 1–10 aralığına sıkıştırır */
export function clampLevel(level: number): number {
  return Math.min(10, Math.max(1, Math.round(level)))
}

/** Seviyeye göre iki uç arasında doğrusal değer */
export function scale(level: number, min: number, max: number): number {
  const l = clampLevel(level)
  return Math.round(min + ((max - min) * (l - 1)) / 9)
}

/**
 * Egzersizlerin Türkçe adları.
 *
 * MOTORDA DURUYOR, uygulamalarda değil. Panelde bir kopyası vardı ve
 * çocuk uygulamasına ikinci bir kopya yazıldığında yarısı eksik
 * kaldı: tablette ekranda `arith_fluency` yazıyordu. Kopya tutmanın
 * bedeli bu.
 *
 * Tipi `Record<ExerciseCode, string>`: yeni bir egzersiz kodu
 * eklendiğinde adı yazılmadıysa DERLEME HATASI veriyor. Eksik ad
 * artık çalışma zamanında ortaya çıkmıyor.
 */
export const EGZERSIZ_ADI: Record<ExerciseCode, string> = {
  scan_target: 'Hedef tarama',
  spot_difference: 'Farkı bul',
  odd_one_out: 'Farklı olanı bul',
  word_search: 'Kelime bulmaca',
  path_trace: 'Yol takibi',
  span_recall: 'Dizi hatırlama',
  memory_pairs: 'Eş bulma',
  symbol_code: 'Şekil-sembol eşleme',
  listening_steps: 'Yönerge takibi',
  reading_target: 'Okuyup sayma',
  matrix_pattern: 'Örüntü tamamlama',
  sequence_complete: 'Diziyi tamamla',
  logic_grid: 'Mantık ızgarası',
  sudoku_mini: 'Sayı bulmaca',
  category_sort: 'Gruplama',
  maze: 'Labirent',
  mental_rotation: 'Zihinde çevirme',
  mirror_image: 'Ayna görüntüsü',
  arith_fluency: 'Toplama çıkarma',
  opposite_rule: 'Tersini yap',
  dual_sequence: 'İç içe dizi',
  arith_grid: 'İşlem karesi',
  trail_making: 'İz sürme',
  word_ladder: 'Kelime merdiveni',
  word_fluency: 'Kurallı kelime üretme',
  dot_copy: 'Şekli kopyala',
  delayed_recall: 'Resme bak ve hatırla',
  overlap_find: 'Üst üste binmiş şekiller',
  hidden_figure: 'Gizli şekli bul',
  tower_of_london: 'Londra Kulesi',
  rule_maze: 'Sayılı labirent',
  code_language: 'Şifreli dil',
  contradiction: 'Çelişkiyi bul',
  schedule_plan: 'Günü planla',
  route_plan: 'Rota planla',
  false_belief: 'Nerede arayacak?',
  social_cue: 'Aslında ne hissetti?',
  alternative_uses: 'Başka ne işe yarar?',
  incomplete_figures: 'Çizimi tamamla',
  track_path: 'Yoldan çıkma',
  rhythm_tap: 'Ritmi tekrarla',
  eye_emotion: 'Gözler ne anlatıyor?',
  zebra_grid: 'Kim hangisi?',
  balance_scale: 'Terazi',
  overlay_layers: 'Üst üste koy',
  paper_fold: 'Katla ve del',
  flow_trace: 'Kuralları izle',
}

/** Kod tanınmıyorsa kodun kendisini döndürür — ekranda boşluk kalmasın */
export function egzersizAdi(kod: string): string {
  return (EGZERSIZ_ADI as Record<string, string>)[kod] ?? kod
}

/**
 * Egzersizin hangi alan ikonunu taşıdığı — çerçeve katmanı.
 *
 * Yedi `Domain`'den TÜRETİLMİYOR, elle yazılıyor. Türetmek denendi ve
 * yanlış çıktı: `maze`'in alanları `working_memory_visual` +
 * `selective_attention`, türetilse bellek ya da dikkat ikonu alırdı;
 * oysa labirent uzamsal bir iş. `symbol_code` aynı şekilde işlem
 * hızına düşüyor. Yedi alan ölçümün ekseni, bu dördü çocuğa ve
 * uygulayıcıya gösterilen gruplama; ikisi aynı şey değil.
 *
 * Tipi `Record<ExerciseCode, AlanKodu>`: yeni bir şablon alan
 * yazmazsa DERLEME HATASI veriyor — `EGZERSIZ_ADI` ile aynı gerekçe.
 */
export const EGZERSIZ_ALANI: Record<ExerciseCode, AlanKodu> = {
  scan_target: 'dikkat',
  spot_difference: 'dikkat',
  odd_one_out: 'dikkat',
  word_search: 'dikkat',
  path_trace: 'dikkat',
  span_recall: 'bellek',
  memory_pairs: 'bellek',
  symbol_code: 'bellek',
  listening_steps: 'bellek',
  reading_target: 'bellek',
  matrix_pattern: 'akil',
  sequence_complete: 'akil',
  logic_grid: 'akil',
  sudoku_mini: 'akil',
  category_sort: 'akil',
  maze: 'uzamsal',
  mental_rotation: 'uzamsal',
  mirror_image: 'uzamsal',
  /**
   * Bu ikisi `spatial.ts` dosyasında duruyor ama uzamsal DEĞİL.
   *
   * Dosya düzeni bir gruplama değil, yazılma sırası. Toplama çıkarma
   * işlem hızı ölçüyor, tersini yap ketleme; ikisinde de uzamda bir
   * şey yok. Küp ikonu "üç boyut" diyor ve bir toplama sayfasının
   * üstünde yalan söylüyordu. Dördün içinde en yakını akıl yürütme:
   * ikisi de bir kuralı uygulama işi.
   */
  arith_fluency: 'akil',
  opposite_rule: 'akil',
  dual_sequence: 'akil',
  arith_grid: 'akil',
  /**
   * İz sürme dikkat: harf-sayı arasında geçiş yapmak esneklik ama
   * çocuğun yaptığı şey sayfada hedef aramak.
   */
  trail_making: 'dikkat',
  /** Kelime işleri bellek: ikisi de kelime dağarcığını tutup tarıyor. */
  word_ladder: 'bellek',
  word_fluency: 'bellek',
  dot_copy: 'uzamsal',
  delayed_recall: 'bellek',
  /**
   * Figür-zemin DİKKAT.
   *
   * İkisi de görsel algı işi ama çocuğun yaptığı şey bütünün içinden
   * hedefi ayıklamak: dördün içinde en yakını dikkat. Uzamsal
   * ikonunu (küp) almaları yanlış olurdu — üç boyut yok, ayrıştırma
   * var.
   */
  overlap_find: 'dikkat',
  hidden_figure: 'dikkat',
  /**
   * Yönetici işlevler AKIL YÜRÜTME.
   *
   * Dördün içinde en yakını bu: ikisi de hamleden önce sonucunu
   * görmeyi istiyor. Kurallı labirent uzamsal bir sayfada duruyor ama
   * ölçtüğü şey yolu bulmak değil, kurala rağmen yolu bulmak —
   * `maze` ile aynı ikonu taşımaları yanlış olurdu.
   */
  tower_of_london: 'akil',
  rule_maze: 'akil',
  code_language: 'akil',
  /** Metni tutarlılık açısından taramak: dikkat işi. */
  contradiction: 'dikkat',
  schedule_plan: 'akil',
  /** Harita okuma uzamsal; ama yapılan iş kısıta rağmen yol kurmak. */
  route_plan: 'akil',
  /**
   * Sosyal biliş BELLEK alanında.
   *
   * Dördün içinde en yakını bu: ikisinde de çocuk bir hikâyeyi
   * tutup üstünde işlem yapıyor. Doğrusu beşinci bir alan açmak
   * olurdu ("sosyal") ama dört ikon çocuğa "bu hafta dört ayrı şey
   * var" demek için seçilmişti; beşinci ikon o sayıyı bozar ve
   * karşılığında çocuğa hiçbir şey kazandırmaz.
   */
  false_belief: 'bellek',
  social_cue: 'bellek',
  /** Iraksak düşünme akıl yürütme: ikisi de kuralı esnetmeyi istiyor. */
  alternative_uses: 'akil',
  incomplete_figures: 'akil',
  /** İz sürme dikkat: yol sayfada duruyor, iş onu takip etmek. */
  track_path: 'dikkat',
  /** Ritim: sırayı tutup geri vermek — bellek işi. */
  rhythm_tap: 'bellek',
  /**
   * Göz ifadesi DİKKAT.
   *
   * Öteki iki sosyal biliş şablonu bellek ikonu taşıyor çünkü ikisinde
   * de tutulup üstünde işlem yapılan bir hikâye var. Burada hikâye yok:
   * çocuğun yaptığı iş bir çizimdeki belirli ayrıntılara (kaş eğimi,
   * kapak açıklığı, bakış) bakmak. Figür-zemin ile aynı gerekçe.
   */
  eye_emotion: 'dikkat',
  /** Tumdengelim ve nicel muhakeme: ikisi de akil yurutme. */
  zebra_grid: 'akil',
  balance_scale: 'akil',
  /**
   * Katman sentezi ve kagit katlama UZAMSAL.
   *
   * Ikisinde de cocugun yaptigi is zihninde bir donusum uygulayip
   * sonucunu gormek; `mental_rotation` ile ayni aile.
   */
  overlay_layers: 'uzamsal',
  paper_fold: 'uzamsal',
  /** Akis semasi: kurali tutup sirayla uygulamak — akil yurutme. */
  flow_trace: 'akil',
}

/** Kod tanınmıyorsa dikkat: ikon boş kalmasın, şerit kaymasın */
export function egzersizAlani(kod: string): AlanKodu {
  return (EGZERSIZ_ALANI as Record<string, AlanKodu>)[kod] ?? 'dikkat'
}
